import React, { useState, useMemo } from 'react';

export default function SalesPerformanceReport({apiData  = [] }) {
  
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Track which salesperson row index is currently expanded to show full text on tap
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    
    if (!apiData) return []; 
  
    let sortableItems = [...apiData];
  
    if (sortConfig && sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
  
        // Handle cases where values might be missing or undefined
        if (aValue === undefined || bValue === undefined) return 0;
  
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
  
    return sortableItems;
  // 2. Deconstruct sortConfig properties so the cache only breaks when values actually change
  }, [apiData, sortConfig?.key, sortConfig?.direction]);

  const totals = useMemo(() => {
    const sums = { gross: 0, disc: 0, net: 0, cost: 0, profit: 0, vat: 0 };
    if(apiData){
        apiData.forEach(item => {
            sums.gross += item["GROSS SALE"] || 0;
            sums.disc += item["DISC"] || 0;
            sums.net += item["NET SALES"] || 0;
            sums.cost += item["COST"] || 0;
            sums.profit += item["PROFIT"] || 0;
            sums.vat += item["VAT AMT"] || 0;
          });
          sums.avgPercentage = sums.cost > 0 ? (sums.profit / sums.cost) * 100 : 0;
    }

    return sums;
    
  }, [apiData]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleRowExpansion = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  return (
    <div style={styles.container}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.thFixedLeft}>SALES MAN</th>
            <th onClick={() => handleSort('PROFIT')} style={styles.thRight}>PROFIT {getSortIcon('PROFIT')}</th>
            <th onClick={() => handleSort('GROSS SALE')} style={styles.thRight}>GROSS SALE {getSortIcon('GROSS SALE')}</th>
            <th onClick={() => handleSort('DISC')} style={styles.thRight}>DISC {getSortIcon('DISC')}</th>
            <th onClick={() => handleSort('NET SALES')} style={styles.thRight}>NET SALES {getSortIcon('NET SALES')}</th>
            <th onClick={() => handleSort('COST')} style={styles.thRight}>COST {getSortIcon('COST')}</th>
            <th onClick={() => handleSort('%AGE')} style={styles.thRight}>%AGE {getSortIcon('%AGE')}</th>
            <th onClick={() => handleSort('VAT AMT')} style={styles.thRight}>VAT AMT {getSortIcon('VAT AMT')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => {
            const bgRowColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
            const cleanName = row["SALES MAN"].trim() || "Unassigned";
            const isExpanded = expandedRowIndex === index;

            // Compute dynamic layouts if this specific column is expanded by the user
            const dynamicColumnStyle = {
              ...styles.tdFixedLeft,
              backgroundColor: bgRowColor,
              width: isExpanded ? '180px' : '90px',
              maxWidth: isExpanded ? '180px' : '90px',
              transition: 'width 0.2s, max-width 0.2s', // Smooth slide animation
            };

            const dynamicTextStyle = {
              ...styles.truncatedText,
              whiteSpace: isExpanded ? 'normal' : 'nowrap', // Wrap if expanded
              wordBreak: 'break-word',
            };

            return (
              <tr key={index} style={{ backgroundColor: bgRowColor }}>
                <td 
                  style={dynamicColumnStyle} 
                  onClick={() => toggleRowExpansion(index)}
                  title={cleanName} // Desktop fallback Native Tooltip
                >
                  <div style={dynamicTextStyle}>
                    {cleanName === "Unassigned" ? <em style={{ color: '#94a3b8' }}>Unassigned</em> : cleanName}
                  </div>
                </td>
                <td style={styles.tdRight}>{formatNumber(row["PROFIT"])}</td>
                <td style={styles.tdRight}>{formatNumber(row["GROSS SALE"])}</td>
                <td style={styles.tdRight}>{formatNumber(row["DISC"])}</td>
                <td style={styles.tdRight}>{formatNumber(row["NET SALES"])}</td>
                <td style={styles.tdRight}>{formatNumber(row["COST"])}</td>
                <td style={styles.tdRight}>{formatNumber(row["%AGE"])}%</td>
                <td style={styles.tdRight}>{formatNumber(row["VAT AMT"])}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={styles.tfFixedLeft}>
              <div style={styles.truncatedText}>TOTAL</div>
            </td>
            <td style={styles.tfRight}>{formatNumber(totals.gross)}</td>
            <td style={styles.tfRight}>{formatNumber(totals.disc)}</td>
            <td style={styles.tfRight}>{formatNumber(totals.net)}</td>
            <td style={styles.tfRight}>{formatNumber(totals.cost)}</td>
            <td style={styles.tfRight}>{formatNumber(totals.profit)}</td>
            <td style={styles.tfRight}>{formatNumber(totals.avgPercentage)}%</td>
            <td style={styles.tfRight}>{formatNumber(totals.vat)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const styles = {
  container: {
    maxHeight: '200px',
    overflowY: 'auto',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontFamily: 'sans-serif',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '11px',
    minWidth: '950px', 
    tableLayout: 'fixed',
  },
  thFixedLeft: {
    position: 'sticky',
    top: 0,
    left: 0,
    backgroundColor: '#f1f5f9',
    zIndex: 30,
    padding: '8px',
    textAlign: 'left',
    color: '#475569',
    fontWeight: '600',
    borderBottom: '2px solid #cbd5e1',
    borderRight: '1px solid #cbd5e1',
    width: '90px',
    maxWidth: '90px',
  },
  thRight: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#f1f5f9',
    zIndex: 20,
    padding: '8px',
    textAlign: 'right',
    color: '#475569',
    fontWeight: '600',
    borderBottom: '2px solid #cbd5e1',
    cursor: 'pointer',
    userSelect: 'none',
    width: '110px',
  },
  tdFixedLeft: {
    position: 'sticky',
    left: 0,
    zIndex: 15,
    padding: '6px 8px',
    textAlign: 'left',
    color: '#334155',
    fontWeight: '500',
    borderBottom: '1px solid #e2e8f0',
    borderRight: '1px solid #cbd5e1',
    cursor: 'pointer', // Changes cursor to hand pointer indicating it's clickable
  },
  tdRight: { 
    padding: '6px 8px', 
    textAlign: 'right', 
    color: '#334155', 
    borderBottom: '1px solid #f1f5f9' 
  },
  tfFixedLeft: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    backgroundColor: '#e2e8f0',
    zIndex: 30,
    padding: '8px',
    textAlign: 'left',
    color: '#1e293b',
    fontWeight: '700',
    borderTop: '2px solid #cbd5e1',
    borderRight: '1px solid #cbd5e1',
    width: '90px',
    maxWidth: '90px',
  },
  tfRight: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#e2e8f0',
    zIndex: 20,
    padding: '8px',
    textAlign: 'right',
    color: '#1e293b',
    fontWeight: '700',
    borderTop: '2px solid #cbd5e1',
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  }
};